import {
  phonePattern,
  englishWordsPattern,
  isValidNationalCode,
} from "@/shared/constants/regex";
import { object, array, number, string, boolean, type InferType } from "yup";
import staticData from "@/public/data/flight/passengers.json";

interface FormMessages {
  en_first_name: { min_length: string; english_only: string; required: string };
  en_last_name: { min_length: string; english_only: string; required: string };
  na_code: { invalid: string; required: string };
  gender: { required: string };
  birth_date: { required: string };
  nationality: { required: string };
  passport_expire: { required: string };
  passport_nationality_code: { required: string };
  passport_number: { required: string };
  passengers: { required: string };
  phone: { invalid: string };
  email: { invalid: string };
}

const messages: FormMessages = staticData.forms.schema;

const dateSchema = object({
  day: string(),
  month: string(),
  year: string(),
});

const passengerSchema = object({
  isForeign: boolean().default(true).required(),
  isInternational: boolean().default(false).required(),
  en_first_name: string()
    .min(
      3,
      messages.en_first_name?.min_length ||
        "First name must be at least 3 characters",
    )
    .matches(
      englishWordsPattern,
      messages.en_first_name?.english_only || "Only English letters allowed",
    )
    .required(messages.en_first_name?.required || "First name is required"),
  en_last_name: string()
    .min(
      3,
      messages.en_last_name?.min_length ||
        "Last name must be at least 3 characters",
    )
    .matches(
      englishWordsPattern,
      messages.en_last_name?.english_only || "Only English letters allowed",
    )
    .required(messages.en_last_name?.required || "Last name is required"),
  na_code: string().when(["isForeign", "isInternational"], {
    is: (isForeign: boolean, isInternational: boolean) =>
      !isForeign && !isInternational,
    then: (schema) =>
      schema
        .test(
          "nationalCodeValidation",
          messages.na_code?.invalid || "Invalid national code",
          isValidNationalCode,
        )
        .required(messages.na_code?.required || "National code is required"),
    otherwise: (schema) => schema.optional(),
  }),
  gender: string().required(messages.gender?.required || "Gender is required"),
  birth_date: dateSchema
    .required(messages.birth_date?.required || "Birth date is required")
    .test(
      "all-fields-required",
      messages.birth_date.required,
      (value) => !!(value && value.day && value.month && value.year),
    ),
  nationality: string().when("isForeign", {
    is: false,
    then: (schema) =>
      schema.required(
        messages.nationality?.required || "Nationality is required",
      ),
    otherwise: (schema) => schema.optional(),
  }),
  passport_expire: dateSchema.when(["isForeign", "isInternational"], {
    is: (isForeign: boolean, isInternational: boolean) =>
      isForeign || isInternational,
    then: (schema) =>
      schema
        .required(
          messages.passport_expire?.required ||
            "Passport expiry date is required",
        )
        .test(
          "all-fields-required",
          messages.passport_expire?.required,
          (value) => !!(value && value.day && value.month && value.year),
        ),
    otherwise: (schema) => schema.optional(),
  }),
  passport_nationality_code: string().when(["isForeign", "isInternational"], {
    is: (isForeign: boolean, isInternational: boolean) =>
      isForeign || isInternational,
    then: (schema) =>
      schema.required(
        messages.passport_nationality_code?.required ||
          "Passport nationality code is required",
      ),
    otherwise: (schema) => schema.optional(),
  }),
  passport_number: string().when(["isForeign", "isInternational"], {
    is: (isForeign: boolean, isInternational: boolean) =>
      isForeign || isInternational,
    then: (schema) =>
      schema.required(
        messages.passport_number?.required || "Passport number is required",
      ),
    otherwise: (schema) => schema.optional(),
  }),
  type: string()
    .oneOf(["adult", "child", "infant"], "Type must be adult, child, or infant")
    .required(),
  price: number().required("قیمت مسافر الزامی می باشد"),
}).required();

export const passengersSchema = object({
  passengers: array()
    .of(passengerSchema)
    .required(
      messages.passengers?.required || "Passenger information is required",
    ),
  phone: string().test(
    "phone-validation",
    messages.phone?.invalid || "Invalid phone number",
    (value) => (value ? phonePattern.test(value) : true),
  ),
  email: string()
    .email(messages.email?.invalid || "Invalid email address")
    .optional(),
  flights: array().of(string()),
});

export type PassengersSchemaType = InferType<typeof passengersSchema>;
export type PassengerSchemaType = InferType<typeof passengerSchema>;
